import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Checkbox,
  CHECKBOX_VALUE_STATE_LABELS,
  CHECKBOX_VALUE_STATES,
  type CheckboxValueState,
} from '@enamelai/design-system'
import {
  CHECKBOX_PREVIEW_STATE_LABELS,
  CHECKBOX_PREVIEW_STATES,
} from '../showcaseConstants'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './CheckboxShowcase.css'

type LiveValueState = {
  checked: boolean
  indeterminate: boolean
}

const getInitialLiveValues = (): Record<
  CheckboxValueState,
  LiveValueState
> => ({
  unchecked: { checked: false, indeterminate: false },
  checked: { checked: true, indeterminate: false },
  indeterminate: { checked: false, indeterminate: true },
})

const getValueStateProps = (
  valueState: CheckboxValueState,
): { checked?: boolean; indeterminate?: boolean } => {
  switch (valueState) {
    case 'checked':
      return { checked: true, indeterminate: false }
    case 'indeterminate':
      return { checked: false, indeterminate: true }
    default:
      return { checked: false, indeterminate: false }
  }
}

const CheckboxShowcase: React.FC = () => {
  const [label, setLabel] = useState('Checkbox label')
  const [disabled, setDisabled] = useState(false)
  const [liveValues, setLiveValues] = useState(getInitialLiveValues)
  const [highlightedValueState, setHighlightedValueState] =
    useState<CheckboxValueState>('unchecked')
  const liveCardRefs = useRef<
    Partial<Record<CheckboxValueState, HTMLDivElement | null>>
  >({})
  const rowRefs = useRef<
    Partial<Record<CheckboxValueState, HTMLDivElement | null>>
  >({})

  useEffect(() => {
    liveCardRefs.current[highlightedValueState]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
    rowRefs.current[highlightedValueState]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [highlightedValueState])

  const handleLiveChange =
    (valueState: CheckboxValueState) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const nextChecked = event.target.checked

      setLiveValues((previous) => ({
        ...previous,
        [valueState]: {
          checked: nextChecked,
          indeterminate: false,
        },
      }))
    }

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'checkbox-highlight-value',
            label: 'Highlighted value',
            control: (
              <select
                id="checkbox-highlight-value"
                className="ui-field-control"
                value={highlightedValueState}
                onChange={(event) =>
                  setHighlightedValueState(
                    event.target.value as CheckboxValueState,
                  )
                }
              >
                {CHECKBOX_VALUE_STATES.map((option) => (
                  <option key={option} value={option}>
                    {CHECKBOX_VALUE_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
        ],
      },
      {
        title: 'Content',
        fields: [
          {
            id: 'checkbox-label',
            label: 'Label',
            control: (
              <input
                id="checkbox-label"
                className="ui-field-control"
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'checkbox-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="checkbox-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [disabled, highlightedValueState, label],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Checkbox"
        description="Unchecked, checked, and indeterminate values with default, hover, focus, and disabled states."
        stageClassName="ui-preview__stage--wide checkbox-showcase__stage-wrap"
      >
        <div className={`checkbox-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              All value states at once — edit the shared label and try each
              checkbox interactively.
            </p>
            <div className="checkbox-showcase__live-grid">
              {CHECKBOX_VALUE_STATES.map((valueState) => (
                <div
                  key={valueState}
                  ref={(element) => {
                    liveCardRefs.current[valueState] = element
                  }}
                  className={`checkbox-showcase__live-card${
                    valueState === highlightedValueState
                      ? ' checkbox-showcase__live-card--selected'
                      : ''
                  }`}
                >
                  <span className="checkbox-showcase__live-card-label">
                    {CHECKBOX_VALUE_STATE_LABELS[valueState]}
                  </span>
                  <Checkbox
                    label={label}
                    checked={liveValues[valueState].checked}
                    indeterminate={liveValues[valueState].indeterminate}
                    disabled={disabled}
                    onChange={handleLiveChange(valueState)}
                  />
                </div>
              ))}
            </div>
          </section>

          <section className="checkbox-showcase__matrix">
            <h4 className="checkbox-showcase__section-title">
              Interaction states
            </h4>
            <div className="checkbox-showcase__matrix-header">
              <span className="checkbox-showcase__matrix-corner">Value</span>
              {CHECKBOX_PREVIEW_STATES.map((state) => (
                <span
                  key={state}
                  className="checkbox-showcase__matrix-header-cell"
                >
                  {CHECKBOX_PREVIEW_STATE_LABELS[state]}
                </span>
              ))}
            </div>

            {CHECKBOX_VALUE_STATES.map((valueState) => (
              <div
                key={valueState}
                ref={(element) => {
                  rowRefs.current[valueState] = element
                }}
                className={`checkbox-showcase__matrix-row${
                  valueState === highlightedValueState
                    ? ' checkbox-showcase__matrix-row--selected'
                    : ''
                }`}
              >
                <span className="checkbox-showcase__matrix-row-label">
                  {CHECKBOX_VALUE_STATE_LABELS[valueState]}
                </span>
                {CHECKBOX_PREVIEW_STATES.map((previewState) => (
                  <div
                    key={`${valueState}-${previewState}`}
                    className="checkbox-showcase__matrix-cell"
                  >
                    <Checkbox
                      {...getValueStateProps(valueState)}
                      previewState={previewState}
                      aria-label={`${CHECKBOX_VALUE_STATE_LABELS[valueState]} ${CHECKBOX_PREVIEW_STATE_LABELS[previewState]}`}
                    />
                  </div>
                ))}
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="Checkbox" sections={propertySections} />
    </>
  )
}

export default CheckboxShowcase
